'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PriceChart } from './PriceChart';
import { StatGrid } from './StatGrid';
import { NewsFeed } from './NewsFeed';
import { formatPrice, formatPercent, formatChange, formatNumber, formatMarketCap, getChangeColor } from '@/lib/terminal-format';
import { ArrowLeft, Lock } from 'lucide-react';
import { TerminalWordmark } from './TerminalWordmark';
import { useSubscription } from '@/components/SubscriptionContext';
import Link from 'next/link';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

export const TABS = ['Overview', 'Financials', 'Risk'] as const;
export type Tab = typeof TABS[number];

interface TerminalResearchProps {
  symbol: string;
  onBack: () => void;
  tab?: Tab;
  onTabChange?: (tab: Tab) => void;
}

export function TerminalResearch({ symbol, onBack, tab: controlledTab, onTabChange }: TerminalResearchProps) {
  const [internalTab, setInternalTab] = useState<Tab>('Overview');
  const tab = controlledTab ?? internalTab;
  const setTab = (t: Tab) => {
    if (onTabChange) onTabChange(t);
    else setInternalTab(t);
  };
  const { tier } = useSubscription();
  const isPaid = tier === 'paid';

  const { data: quote, isLoading: quoteLoading } = useQuery<Record<string, number | string>>({
    queryKey: ['/api/terminal/quote', symbol],
    queryFn: async () => {
      const res = await fetch(`/api/terminal/quote/${encodeURIComponent(symbol)}`);
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    refetchInterval: 30000,
  });

  const { data: financials, isLoading: finLoading } = useQuery<Record<string, unknown>>({
    queryKey: ['/api/terminal/financials', symbol],
    queryFn: async () => {
      const res = await fetch(`/api/terminal/financials/${encodeURIComponent(symbol)}`);
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    refetchInterval: 300000,
  });


  return (
    <div className="h-full flex flex-col bg-[#0A0E17]">
      {/* Header */}
      <div className="min-h-12 flex items-center justify-between px-3 py-2 md:h-12 md:px-4 md:py-0 bg-[#111827] border-b border-[#1F2937] flex-shrink-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <button
            onClick={onBack}
            className="text-[#6B7280] hover:text-[var(--color-accent)] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
          <TerminalWordmark />
          <span className="text-[#6B7280] text-xs font-mono">/</span>
          <span className="font-mono text-xs font-semibold tracking-wider text-[var(--color-accent)]">{symbol}</span>
          {quote && (
            <span className="hidden sm:inline text-[10px] text-[#6B7280]">{quote.exchangeName as string}</span>
          )}
          {quoteLoading ? (
            <div className="h-4 w-32 bg-[#1F2937] animate-pulse rounded" />
          ) : quote ? (
            <div className="flex items-center gap-2 ml-1">
              <span className="font-mono text-sm font-bold tabular-nums text-[#E5E7EB]">
                {formatPrice(quote.price as number)}
              </span>
              <span className={`font-mono text-xs tabular-nums ${getChangeColor(quote.change as number)}`}>
                {formatChange(quote.change as number)}
              </span>
              <span className={`font-mono text-xs tabular-nums ${getChangeColor(quote.changePercent as number)}`}>
                ({formatPercent(quote.changePercent as number)})
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-0 border-b border-[#1F2937] bg-[#111827] flex-shrink-0">
        {TABS.map((t) => {
          const isLocked = !isPaid && (t === 'Financials' || t === 'Risk');
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors border-b-2 flex items-center gap-1.5 ${
                tab === t
                  ? 'text-[var(--color-accent)] border-[var(--color-accent)]'
                  : 'text-[#6B7280] border-transparent hover:text-[#E5E7EB]'
              }`}
            >
              {t}
              {isLocked && <Lock className="w-3 h-3" />}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'Overview' && (
          <OverviewTab symbol={symbol} quote={quote} quoteLoading={quoteLoading} />
        )}
        {tab === 'Financials' && (
          isPaid ? (
            <FinancialsTab financials={financials} isLoading={finLoading} />
          ) : (
            <PaidGate feature="Financials" />
          )
        )}
        {tab === 'Risk' && (
          isPaid ? (
            <RiskTab quote={quote} financials={financials} isLoading={quoteLoading || finLoading} />
          ) : (
            <PaidGate feature="Risk Analysis" />
          )
        )}
      </div>
    </div>
  );
}

function OverviewTab({ symbol, quote, quoteLoading }: { symbol: string; quote: Record<string, number | string> | undefined; quoteLoading: boolean }) {
  const stats = quote
    ? [
        { label: 'Market Cap', value: formatMarketCap(quote.marketCap as number) },
        { label: 'P/E Ratio', value: quote.pe ? (quote.pe as number).toFixed(2) : 'N/A' },
        { label: 'EPS', value: quote.eps ? (quote.eps as number).toFixed(2) : 'N/A' },
        { label: 'Beta', value: quote.beta ? (quote.beta as number).toFixed(2) : 'N/A' },
        { label: '52W High', value: formatPrice(quote.fiftyTwoWeekHigh as number) },
        { label: '52W Low', value: formatPrice(quote.fiftyTwoWeekLow as number) },
        { label: 'Volume', value: formatNumber(quote.volume as number, 1) },
        { label: 'Avg Volume', value: formatNumber(quote.avgVolume as number, 1) },
        { label: 'Day High', value: formatPrice(quote.dayHigh as number) },
        { label: 'Day Low', value: formatPrice(quote.dayLow as number) },
        { label: 'Open', value: formatPrice(quote.open as number) },
        { label: 'Prev Close', value: formatPrice(quote.previousClose as number) },
      ]
    : [];

  return (
    <div className="space-y-4">
      <PriceChart symbol={symbol} height={320} defaultRange="1M" />
      <StatGrid items={stats} columns={4} isLoading={quoteLoading} />
      <div className="bg-[#111827] border border-[#1F2937]">
        <div className="px-3 py-2 border-b border-[#1F2937]">
          <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">Recent News</span>
        </div>
        <NewsFeed symbol={symbol} limit={6} />
      </div>
    </div>
  );
}

function FinancialsTab({ financials, isLoading }: { financials: Record<string, unknown> | undefined; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-48 w-full bg-[#1F2937] animate-pulse rounded" />
        <div className="h-24 w-full bg-[#1F2937] animate-pulse rounded" />
      </div>
    );
  }

  const f = financials || {};
  const revenueData = ((f.revenue as { date: string; value: number }[]) || []).map((r) => ({
    date: r.date,
    revenue: r.value / 1e9,
  }));

  const profitStats = [
    { label: 'Gross Margin', value: f.grossMargins ? ((f.grossMargins as number) * 100).toFixed(1) + '%' : 'N/A', color: (f.grossMargins as number) > 0 ? 'text-terminal-positive' : '' },
    { label: 'Operating Margin', value: f.operatingMargins ? ((f.operatingMargins as number) * 100).toFixed(1) + '%' : 'N/A', color: (f.operatingMargins as number) > 0 ? 'text-terminal-positive' : '' },
    { label: 'Net Margin', value: f.profitMargins ? ((f.profitMargins as number) * 100).toFixed(1) + '%' : 'N/A', color: (f.profitMargins as number) > 0 ? 'text-terminal-positive' : '' },
    { label: 'ROE', value: f.returnOnEquity ? ((f.returnOnEquity as number) * 100).toFixed(1) + '%' : 'N/A', color: (f.returnOnEquity as number) > 0 ? 'text-terminal-positive' : '' },
  ];

  const balanceStats = [
    { label: 'Total Cash', value: formatNumber(f.totalCash as number) },
    { label: 'Total Debt', value: formatNumber(f.totalDebt as number) },
    { label: 'Debt/Equity', value: f.debtToEquity ? (f.debtToEquity as number).toFixed(1) : 'N/A' },
    { label: 'Current Ratio', value: f.currentRatio ? (f.currentRatio as number).toFixed(2) : 'N/A' },
  ];

  const cashflowStats = [
    { label: 'Operating CF', value: formatNumber(f.operatingCashflow as number), color: (f.operatingCashflow as number) > 0 ? 'text-terminal-positive' : 'text-terminal-negative' },
    { label: 'Free CF', value: formatNumber(f.freeCashflow as number), color: (f.freeCashflow as number) > 0 ? 'text-terminal-positive' : 'text-terminal-negative' },
    { label: 'Revenue Growth', value: f.revenueGrowth ? ((f.revenueGrowth as number) * 100).toFixed(1) + '%' : 'N/A', color: (f.revenueGrowth as number) > 0 ? 'text-terminal-positive' : 'text-terminal-negative' },
    { label: 'Earnings Growth', value: f.earningsGrowth ? ((f.earningsGrowth as number) * 100).toFixed(1) + '%' : 'N/A', color: (f.earningsGrowth as number) > 0 ? 'text-terminal-positive' : 'text-terminal-negative' },
  ];

  return (
    <div className="space-y-4">
      {revenueData.length > 0 && (
        <div className="bg-[#111827] border border-[#1F2937] p-4">
          <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">Revenue (Billions)</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={revenueData} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                tickFormatter={(v) => v.toFixed(0) + 'B'}
                width={50}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1A2035', border: '1px solid #1F2937', borderRadius: '2px', fontSize: '12px', fontFamily: 'JetBrains Mono', color: '#E5E7EB' }}
                formatter={(value: number | undefined) => [(value?.toFixed(2) ?? '0') + 'B', 'Revenue']}
              />
              <Bar dataKey="revenue" fill="var(--color-accent)" radius={[1, 1, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-[#111827] border border-[#1F2937]">
        <div className="px-3 py-2 border-b border-[#1F2937]">
          <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">Profitability</span>
        </div>
        <StatGrid items={profitStats} columns={4} />
      </div>

      <div className="bg-[#111827] border border-[#1F2937]">
        <div className="px-3 py-2 border-b border-[#1F2937]">
          <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">Balance Sheet</span>
        </div>
        <StatGrid items={balanceStats} columns={4} />
      </div>

      <div className="bg-[#111827] border border-[#1F2937]">
        <div className="px-3 py-2 border-b border-[#1F2937]">
          <span className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider">Cash Flow</span>
        </div>
        <StatGrid items={cashflowStats} columns={4} />
      </div>
    </div>
  );
}

function RiskTab({ quote, financials, isLoading }: { quote: Record<string, number | string> | undefined; financials: Record<string, unknown> | undefined; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-48 w-full bg-[#1F2937] animate-pulse rounded" />
      </div>
    );
  }

  const f = financials || {};
  const q = quote || {};
  const beta = (f.beta as number) || 0;
  const shortFloat = (f.shortPercentOfFloat as number) || 0;
  const high52 = (q.fiftyTwoWeekHigh as number) || 0;
  const low52 = (q.fiftyTwoWeekLow as number) || 0;
  const price = (q.price as number) || 0;
  const rangeWidth = high52 - low52;
  const pricePosition = rangeWidth > 0 ? ((price - low52) / rangeWidth) * 100 : 50;

  return (
    <div className="space-y-4">
      {/* Beta Gauge */}
      <div className="bg-[#111827] border border-[#1F2937] p-4">
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">Beta</div>
        <div className="flex items-center gap-4">
          <div className="relative w-32 h-32">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#1F2937" strokeWidth="6" />
              <circle
                cx="50" cy="50" r="42"
                fill="none"
                stroke={beta > 1.5 ? '#FF1744' : beta > 1 ? '#F59E0B' : '#00C853'}
                strokeWidth="6"
                strokeDasharray={`${Math.min(beta / 2, 1) * 264} 264`}
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
              />
              <text x="50" y="50" textAnchor="middle" dominantBaseline="middle"
                fill="#E5E7EB" fontSize="16" fontFamily="JetBrains Mono" fontWeight="bold">
                {beta.toFixed(2)}
              </text>
            </svg>
          </div>
          <div className="text-xs text-[#6B7280] space-y-1">
            <div>{'< 1.0'}: Lower volatility than market</div>
            <div>{'= 1.0'}: Same volatility as market</div>
            <div>{'> 1.0'}: Higher volatility than market</div>
          </div>
        </div>
      </div>

      {/* Short Interest */}
      <div className="bg-[#111827] border border-[#1F2937] p-4">
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">Short Interest (% of Float)</div>
        <div className="flex items-center gap-4">
          <span className="font-mono text-2xl font-bold tabular-nums text-[#E5E7EB]">
            {(shortFloat * 100).toFixed(2)}%
          </span>
          <div className="flex-1 h-2 bg-[#1F2937] rounded-sm overflow-hidden">
            <div
              className="h-full rounded-sm"
              style={{
                width: `${Math.min(shortFloat * 100 * 5, 100)}%`,
                backgroundColor: shortFloat > 0.1 ? '#FF1744' : shortFloat > 0.05 ? '#F59E0B' : '#00C853',
              }}
            />
          </div>
        </div>
      </div>

      {/* 52-Week Range */}
      <div className="bg-[#111827] border border-[#1F2937] p-4">
        <div className="text-[10px] font-semibold text-[var(--color-accent)] uppercase tracking-wider mb-3">52-Week Range</div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs tabular-nums text-[#6B7280]">{formatPrice(low52)}</span>
          <div className="flex-1 relative h-3">
            <div className="absolute inset-0 bg-[#1F2937] rounded-sm" />
            <div
              className="absolute top-0 bottom-0 bg-[#3B82F6] rounded-sm"
              style={{ left: 0, width: `${pricePosition}%` }}
            />
            <div
              className="absolute top-1/2 w-2.5 h-2.5 bg-[var(--color-accent)] rounded-full border-2 border-[#0A0E17]"
              style={{ left: `${pricePosition}%`, transform: 'translate(-50%, -50%)' }}
            />
          </div>
          <span className="font-mono text-xs tabular-nums text-[#6B7280]">{formatPrice(high52)}</span>
        </div>
        <div className="text-center mt-2">
          <span className="font-mono text-sm font-bold tabular-nums text-[#E5E7EB]">{formatPrice(price)}</span>
        </div>
      </div>

      {/* Debt Metrics */}
      <StatGrid
        items={[
          { label: 'Debt/Equity', value: f.debtToEquity ? (f.debtToEquity as number).toFixed(1) : 'N/A' },
          { label: 'Current Ratio', value: f.currentRatio ? (f.currentRatio as number).toFixed(2) : 'N/A' },
          { label: 'Total Debt', value: formatNumber((f.totalDebt as number) || 0) },
          { label: 'Total Cash', value: formatNumber((f.totalCash as number) || 0) },
        ]}
        columns={4}
      />
    </div>
  );
}


function PaidGate({ feature }: { feature: string }) {
  return (
    <div className="flex items-center justify-center min-h-[300px]">
      <div className="text-center max-w-sm">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 20%, transparent)' }}>
          <Lock className="w-6 h-6 text-[var(--color-accent)]" />
        </div>
        <h3 className="text-lg font-semibold text-[#E5E7EB] mb-2">{feature}</h3>
        <p className="text-sm text-[#6B7280] mb-4">
          <Link href="/checkout" className="font-medium text-[var(--color-accent)] hover:underline">Upgrade to Pro</Link>{' '}
          to access {feature.toLowerCase()} data and insights.
        </p>
      </div>
    </div>
  );
}
